import React, { useState } from "react";
import PropTypes from "prop-types";
import { AnimatePresence } from "framer-motion";
import { LayoutGrid, List, Grid3x3 } from "lucide-react";
import Tooltip from "./ui/Tooltip";
import Button from "./ui/Button";
import { cn } from "../utils/classNames";

const ViewModeSwitcher = ({ viewMode, onChange, className }) => {
  const [hoveredMode, setHoveredMode] = useState(null);
  const [anchorElement, setAnchorElement] = useState(null);

  // Don't render if no onChange handler (mobile)
  if (!onChange) {
    return null;
  }

  const modes = [
    { value: "grid", icon: LayoutGrid, label: "Grid" },
    { value: "compact", icon: Grid3x3, label: "Compact" },
    { value: "list", icon: List, label: "List" },
  ];

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 p-1",
        "bg-card border border-accent-gold/70 rounded-md",
        className
      )}
      role="group"
      aria-label="View mode"
    >
      {modes.map(({ value, icon: Icon, label }) => {
        const isActive = viewMode === value;
        return (
          <div
            key={value}
            className="relative"
            onMouseEnter={(e) => {
              setHoveredMode(value);
              setAnchorElement(e.currentTarget.querySelector('button'));
            }}
            onMouseLeave={() => {
              setHoveredMode(null);
              setAnchorElement(null);
            }}
          >
            <Button
              type="button"
              size="icon"
              variant={isActive ? "filter-active" : "filter"}
              onClick={() => onChange(value)}
              className="h-11 w-11"
              aria-label={`Switch to ${label} view`}
              aria-pressed={isActive}
            >
              <Icon className="h-4 w-4" />
            </Button>
            <AnimatePresence>
              {hoveredMode === value && anchorElement && (
                <Tooltip
                  position="bottom"
                  className="max-w-[240px] px-3 py-2"
                  contentClassName="text-caption"
                  anchorRef={{ current: anchorElement }}
                  onClose={() => setHoveredMode(null)}
                >
                  {label}
                </Tooltip>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

ViewModeSwitcher.propTypes = {
  viewMode: PropTypes.oneOf(["grid", "compact", "list"]).isRequired,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default ViewModeSwitcher;
